import { useEffect, useRef } from 'preact/hooks'
import styles from './tabs.module.css'

interface Tab<T extends string> {
  readonly id: T
  readonly label: string
}

interface TabsProps<T extends string> {
  readonly tabs: readonly Tab<T>[]
  readonly value: T
  readonly onChange: (id: T) => void
  /** Focuses the selected tab on mount, for keyboard and gamepad. */
  readonly autoFocus?: boolean
}

/** A tab list with a roving tabindex. Arrow keys move the selection. */
export function Tabs<T extends string>({ tabs, value, onChange, autoFocus = false }: TabsProps<T>) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  const selected = tabs.findIndex((tab) => tab.id === value)

  useEffect(() => {
    if (autoFocus) buttons.current[selected]?.focus()
  }, [autoFocus, selected])

  const move = (index: number, delta: number): void => {
    const nextIndex = (index + delta + tabs.length) % tabs.length
    const next = tabs[nextIndex]
    if (!next) return
    onChange(next.id)
    buttons.current[nextIndex]?.focus()
  }

  return (
    <div class={styles.tabs} role="tablist">
      {tabs.map((tab, index) => {
        const active = tab.id === value
        return (
          <button
            key={tab.id}
            ref={(node) => {
              buttons.current[index] = node
            }}
            type="button"
            role="tab"
            class={styles.tab}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => {
              onChange(tab.id)
            }}
            onKeyDown={(event) => {
              const delta =
                event.key === 'ArrowRight' || event.key === 'ArrowDown'
                  ? 1
                  : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                    ? -1
                    : 0
              if (delta === 0) return
              event.preventDefault()
              move(index, delta)
            }}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
